import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Link,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { formatPhoneNumber } from '@fortress-technology-solutions/fortress-component-library/utils';
import type { MessagingHubItemProps } from './types';
import { formatMessageTimestamp } from './utils';
import {
  conversationIndicatorStatus,
  normalizeConversationStatus,
} from '../../utils/texting';
import useIntl from '../../hooks/useIntl';
import intlMessages from './messages';

function MessagingHubItem({
  conversation,
  isSelected,
  onSelect,
  isNeedsHelpBadgeShown,
}: MessagingHubItemProps): React$Node {
  const intl = useIntl();
  const currentOrigin = window.location.origin;
  const { recipient, lastMessage } = conversation;

  // Determine display data - prefer customer over prospect
  const customer = recipient.customer;
  const prospect = recipient.prospect;
  const property = recipient.property;

  const displayName =
    customer?.fullName || prospect?.fullName || 'Unknown Recipient';
  const lifecycleStatus =
    customer?.lifecycleStatus || prospect?.lifecycleStatus || '';
  const propertyName = property?.name || '';
  const phoneNumber = recipient.phoneNumber
    ? formatPhoneNumber(recipient.phoneNumber.slice(-10))
    : '';

  const { residentId, applicationId, prospectId } = recipient;
  const propertyId = property?.id;
  const profileUrl = useMemo(() => {
    if (!propertyId) return null;
    if (lifecycleStatus === 'Resident' && residentId) {
      return `${currentOrigin}/property/${propertyId}/resident/${residentId}`;
    } else if (lifecycleStatus === 'Applicant' && applicationId) {
      return `${currentOrigin}/property/${propertyId}/application/${applicationId}`;
    } else if (lifecycleStatus === 'Lead' && prospectId) {
      return `${currentOrigin}/property/${propertyId}/prospect/${prospectId}`;
    } else if (lifecycleStatus === 'Prior Resident' && residentId) {
      return `${currentOrigin}/property/${propertyId}/prior-resident/${residentId}`;
    }
    return null;
  }, [
    lifecycleStatus,
    residentId,
    applicationId,
    prospectId,
    currentOrigin,
    propertyId,
  ]);

  const lifecycleStatusLabel =
    lifecycleStatus === 'Lead'
      ? intl.formatMessage(intlMessages.prospectStatus)
      : lifecycleStatus;

  // Build lifecycle status line: "Applicant · Affinity Gardens"
  const lifecycleStatusLine = [lifecycleStatusLabel, propertyName]
    .filter(Boolean)
    .join(' · ');

  // Map conversation status to BadgeStatus props
  const getBadgeStatusProps = (rawStatus: string) => {
    // `pending` is the library's purple pairing; the label is what identifies it, and it must
    // be checked before normalising, which collapses NEEDS_HELP onto UNRESOLVED. The test id is
    // explicit because BadgeStatus emits none of its own, which would leave automation matching
    // on the label text. Deliberately NOT `needs-help-badge`: TextingIndicator already owns that
    // id for the grid's count badge, and one id on two different elements is worse for
    // automation than no id at all.
    if (rawStatus === 'NEEDS_HELP')
      return {
        status: 'pending',
        children: 'Leslie Needs Help',
        'data-testid': 'needs-help-status',
      };

    switch (normalizeConversationStatus(rawStatus)) {
      case 'NEW':
        return { status: 'waitlist', children: 'New' };
      case 'RESOLVED':
        return { status: 'approved', children: 'Resolved' };
      case 'UNRESOLVED':
        return { status: 'alert', children: 'Unresolved' };
      default:
        return { status: 'off', children: rawStatus };
    }
  };

  const badgeProps = getBadgeStatusProps(
    conversationIndicatorStatus(conversation, isNeedsHelpBadgeShown),
  );

  return (
    <Box
      onClick={onSelect}
      sx={{
        padding: 2,
        paddingBottom: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: isSelected ? 'primary.lighter' : 'transparent',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: isSelected ? 'primary.lighter' : 'action.hover',
        },
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Box display="flex" flexDirection="column" width="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Stack flex={1}>
            {profileUrl ? (
              <Link
                to={profileUrl}
                target="_blank"
                underline="hover"
                rel="noopener noreferrer"
                sx={{
                  width: 'fit-content',
                  cursor: 'pointer',
                }}
              >
                <Typography variant="titleMedium">{displayName}</Typography>
              </Link>
            ) : (
              <Typography variant="titleMedium">{displayName}</Typography>
            )}

            {lifecycleStatusLine && (
              <Typography variant="bodySmall">{lifecycleStatusLine}</Typography>
            )}

            {phoneNumber && (
              <Typography variant="bodySmall">{phoneNumber}</Typography>
            )}
          </Stack>

          <BadgeStatus {...badgeProps} variant="filled" size="small" />
        </Box>

        {lastMessage && (
          <Box mt={1}>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '1.5em',
              }}
            >
              {lastMessage.content}
            </Typography>
            <Typography
              variant="bodySmall"
              color="text.disabled"
              sx={{
                display: 'block',
                textAlign: 'right',
                mt: 0.7,
              }}
            >
              {formatMessageTimestamp(lastMessage.createdAt)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MessagingHubItem;
